import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ActionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  points: number;
  message?: string;
}

export const ActionSuccessModal = ({
  isOpen,
  onClose,
  title,
  points,
  message,
}: ActionSuccessModalProps) => {
  const [showPoints, setShowPoints] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Delay showing points for checkmark animation
      const timer = setTimeout(() => setShowPoints(true), 400);
      return () => clearTimeout(timer);
    } else {
      setShowPoints(false);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 mx-auto max-w-sm"
          >
            <div className="bg-card rounded-2xl p-8 text-center shadow-xl">
              {/* Checkmark circle */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 15, stiffness: 300, delay: 0.1 }}
                className="mx-auto w-20 h-20 rounded-full bg-success/20 flex items-center justify-center mb-6"
              >
                <motion.div
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 300, delay: 0.2 }}
                  className="w-14 h-14 rounded-full bg-success flex items-center justify-center"
                >
                  <Check className="w-8 h-8 text-white" strokeWidth={3} />
                </motion.div>
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xl font-bold text-foreground mb-2"
              >
                {title}
              </motion.h2>

              {/* Message */}
              {message && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-muted-foreground mb-4"
                >
                  {message}
                </motion.p>
              )}

              {/* Points earned with fly-up animation */}
              <AnimatePresence>
                {showPoints && (
                  <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.5 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="mb-6"
                  >
                    <div className="inline-flex items-center gap-2 bg-accent/20 rounded-full px-6 py-3">
                      <motion.span
                        initial={{ scale: 1 }}
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="text-2xl font-bold text-accent-foreground"
                      >
                        +{points}
                      </motion.span>
                      <span className="text-accent-foreground font-medium">points</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Continue button */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Button onClick={onClose} className="w-full" size="lg">
                  Continue
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
